<template>
  <div class="stock-view-page">
    <StockDocumentPageHeading
      :title="$t('STOCK.TRANSPORT_ORDERS.PAGE_TITLE')"
      :code="isNew ? '' : document.code"
      :new-label="isNew ? $t('STOCK.TRANSPORT_ORDERS.NEW') : ''"
    />
    <v-card :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#details">{{ document.code || $t("STOCK.TRANSPORT_ORDERS.NEW") }}</v-tab>
        <v-tab v-if="!isNew" href="#shipments">{{ $t("STOCK.TRANSPORT_ORDERS.TAB_SHIPMENTS") }}</v-tab>
        <v-tab v-if="!isNew" href="#files">{{ $t("STOCK.TRANSPORT_ORDERS.TAB_FILES") }}</v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="document.status" class="mr-2" :color="statusColor" small dark>
            {{ statusLabel }}
          </v-chip>
          <v-btn
            v-if="canBookToTransit"
            class="mr-2 orange darken-2 white--text"
            small
            @click="createTransitReceipt"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.BOOK_TO_TRANSIT") }}
          </v-btn>
          <v-btn
            v-if="canReceiveToWarehouse"
            class="mr-2 success"
            small
            @click="openReceiveToWarehouseDialog"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.RECEIVE_TO_WAREHOUSE") }}
          </v-btn>
          <v-btn
            v-if="canCreateReceipt && !isMultiStop"
            class="mr-2 primary"
            small
            @click="createReceipt()"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.CREATE_RECEIPT") }}
          </v-btn>
          <v-btn
            v-if="canCreateDelivery && !isMultiStop"
            class="mr-2 primary"
            small
            @click="createDelivery()"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.CREATE_DELIVERY") }}
          </v-btn>
          <v-btn
            v-if="canSend"
            class="mr-2 primary"
            small
            @click="sendOrder"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.SEND") }}
          </v-btn>
          <v-btn
            v-if="canConfirm"
            class="mr-2 success"
            small
            @click="confirmOrder"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.CONFIRM") }}
          </v-btn>
          <v-btn
            v-if="canMarkInTransit"
            class="mr-2 orange darken-2 white--text"
            small
            @click="markInTransit"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.MARK_IN_TRANSIT") }}
          </v-btn>
          <v-btn
            v-if="canMarkDelivered"
            class="mr-2 success"
            small
            @click="markDelivered"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.MARK_DELIVERED") }}
          </v-btn>
          <v-btn
            v-if="canDiscard"
            class="mr-2 error"
            small
            @click="discardOrder"
          >
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn
            v-if="canCancel"
            class="mr-2 error"
            small
            @click="cancelOrder"
          >
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            v-if="canEditDocument"
            class="mr-2 success"
            small
            @click="save"
          >
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="details">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.organization_id"
                    :items="organizations"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION') + '*'"
                    :disabled="headerFieldDisabled('organization_id')"
                    :rules="headerFieldDisabled('organization_id') ? [] : [validations.required()]"
                    :error-messages="validationErrors.organization_id || []"
                    @change="clearHeaderValidationError('organization_id')"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.carrier_company_id"
                    :items="companyOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.TRANSPORT_ORDERS.CARRIER')"
                    :disabled="headerFieldDisabled('carrier_company_id')"
                    cache-items
                    clearable
                    @update:search-input="onCompanySearch"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="document.deal_id"
                    :items="dealOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.TRANSPORT_ORDERS.DEAL')"
                    :disabled="headerFieldDisabled('deal_id')"
                    clearable
                    @change="onDealChange"
                  ></v-autocomplete>
                  <StockDealReference
                    :deal-id="document.deal_id"
                    :deal="document.deal || selectedDealOption"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="document.direction"
                    :items="directionOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('STOCK.TRANSPORT_ORDERS.DIRECTION') + '*'"
                    :hint="isDirectionLocked ? $t('STOCK.TRANSPORT_ORDERS.DIRECTION_LOCKED_HINT') : undefined"
                    :persistent-hint="isDirectionLocked"
                    :disabled="headerFieldDisabled('direction')"
                  ></v-select>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="document.transport_mode"
                    :items="transportModeOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('STOCK.TRANSPORT_ORDERS.TRANSPORT_MODE') + '*'"
                    :disabled="headerFieldDisabled('transport_mode')"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <DatePicker
                    v-model="document.scheduled_loading_at"
                    :label="$t('STOCK.TRANSPORT_ORDERS.SCHEDULED_LOADING')"
                    :disabled="headerFieldDisabled('scheduled_loading_at')"
                    without-hint
                    clearable
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="3">
                  <DatePicker
                    v-model="document.eta_at"
                    :label="$t('STOCK.TRANSPORT_ORDERS.ETA')"
                    :disabled="headerFieldDisabled('eta_at')"
                    without-hint
                    clearable
                  ></DatePicker>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="document.currency_code"
                    :label="$t('STOCK.TRANSPORT_ORDERS.CURRENCY')"
                    :disabled="headerFieldDisabled('currency_code')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-subheader class="px-0 mt-2 d-flex align-center">
                  <span>{{ $t("STOCK.TRANSPORT_ORDERS.STOPS") }}</span>
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    class="mr-2"
                    :disabled="!canEditLines"
                    @click="addStop('loading')"
                  >
                    {{ $t("STOCK.TRANSPORT_ORDERS.ADD_LOADING_STOP") }}
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    :disabled="!canEditLines"
                    @click="addStop('unloading')"
                  >
                    {{ $t("STOCK.TRANSPORT_ORDERS.ADD_UNLOADING_STOP") }}
                  </v-btn>
                </v-subheader>

                <v-alert v-if="stopWarnings.length" type="warning" dense text class="mb-3">
                  <div>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_BALANCE_WARNING_TITLE") }}</div>
                  <ul class="mb-0">
                    <li v-for="(warning, warningIndex) in stopWarnings" :key="warningIndex">
                      {{ warning }}
                    </li>
                  </ul>
                </v-alert>

                <v-simple-table dense class="mb-4 stops-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{{ $t("GENERAL.LABELS.TYPE") }}</th>
                      <th>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_ADDRESS_SOURCE") }}</th>
                      <th>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_GOODS_DESCRIPTION") }}</th>
                      <th>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_REFERENCE_NUMBER") }}</th>
                      <th>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_SCHEDULED_AT") }}</th>
                      <th>{{ $t("STOCK.TRANSPORT_ORDERS.STOP_NOTES") }}</th>
                      <th v-if="!isNew" class="text-center">
                        {{ $t("STOCK.TRANSPORT_ORDERS.STOP_FULFILLED") }}
                      </th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(stop, stopIndex) in stops" :key="stop._key">
                      <td>{{ stopIndex + 1 }}</td>
                      <td>
                        <v-chip
                          x-small
                          dark
                          :color="stop.stop_type === 'loading' ? 'primary' : 'success'"
                        >
                          {{
                            stop.stop_type === "loading"
                              ? $t("STOCK.TRANSPORT_ORDERS.STOP_TYPE_LOADING")
                              : $t("STOCK.TRANSPORT_ORDERS.STOP_TYPE_UNLOADING")
                          }}
                        </v-chip>
                      </td>
                      <td class="stop-address-cell">
                        <v-select
                          v-model="stop.address_source"
                          :items="stopAddressSourceOptions"
                          dense
                          hide-details
                          solo
                          flat
                          class="mb-1"
                          :disabled="!canEditLines"
                          @change="onStopAddressSourceChange(stop)"
                        ></v-select>
                        <v-autocomplete
                          v-if="stop.address_source === 'warehouse'"
                          :key="`${stop._key}-warehouse`"
                          v-model="stop.stock_warehouse_id"
                          :items="stopWarehouseOptions"
                          item-text="label"
                          item-value="id"
                          dense
                          hide-details
                          solo
                          flat
                          :disabled="!canEditLines"
                          @change="onStopWarehouseChange(stop)"
                        ></v-autocomplete>
                        <v-autocomplete
                          v-else-if="stop.address_source === 'company'"
                          :key="`${stop._key}-company`"
                          v-model="stop.company_id"
                          :items="companyOptions"
                          item-text="label"
                          item-value="id"
                          dense
                          hide-details
                          solo
                          flat
                          cache-items
                          :disabled="!canEditLines"
                          @change="onStopCompanyChange(stop)"
                          @update:search-input="onCompanySearch"
                        ></v-autocomplete>
                        <v-textarea
                          v-model="stop.address_override"
                          rows="1"
                          auto-grow
                          dense
                          hide-details
                          solo
                          flat
                          :placeholder="
                            stop.address_source === 'free_text'
                              ? ''
                              : $t('STOCK.TRANSPORT_ORDERS.STOP_ADDRESS_SOURCE')
                          "
                          :disabled="!canEditLines"
                        ></v-textarea>
                      </td>
                      <td>
                        <StockLineField
                          v-model="stop.goods_description"
                          :disabled="!canEditLines"
                        />
                      </td>
                      <td>
                        <StockLineField
                          v-model="stop.reference_number"
                          :disabled="!canEditLines"
                        />
                      </td>
                      <td>
                        <DatePicker
                          v-model="stop.scheduled_at"
                          without-hint
                          clearable
                          :disabled="!canEditLines"
                        ></DatePicker>
                      </td>
                      <td>
                        <StockLineField
                          v-model="stop.notes"
                          :disabled="!canEditLines"
                        />
                      </td>
                      <td v-if="!isNew" class="text-center">
                        <span
                          v-if="!isStopFulfillmentTracked(stop)"
                          class="caption grey--text"
                          :title="$t('STOCK.TRANSPORT_ORDERS.STOP_FULFILLMENT_NOT_TRACKED_HINT')"
                        >
                          {{ $t("STOCK.TRANSPORT_ORDERS.STOP_FULFILLMENT_NOT_TRACKED") }}
                        </span>
                        <v-icon v-else-if="stop.is_fulfilled" color="success" small>
                          mdi-check-circle
                        </v-icon>
                        <v-icon v-else small>mdi-circle-outline</v-icon>
                      </td>
                      <td class="text-nowrap stop-row-actions">
                        <v-btn
                          v-if="canCreateReceiptForStop(stop)"
                          class="mr-1 primary"
                          x-small
                          :disabled="!stop.id"
                          @click="createReceipt(stop)"
                        >
                          {{
                            $t("STOCK.TRANSPORT_ORDERS.CREATE_RECEIPT_FOR_STOP", {
                              no: stopIndex + 1,
                            })
                          }}
                        </v-btn>
                        <v-btn
                          v-if="canCreateDeliveryForStop(stop)"
                          class="mr-1 primary"
                          x-small
                          :disabled="!stop.id"
                          @click="createDelivery(stop)"
                        >
                          {{
                            $t("STOCK.TRANSPORT_ORDERS.CREATE_DELIVERY_FOR_STOP", {
                              no: stopIndex + 1,
                            })
                          }}
                        </v-btn>
                        <v-btn
                          icon
                          small
                          :disabled="!canEditLines"
                          :title="$t('STOCK.TRANSPORT_ORDERS.REMOVE_STOP')"
                          @click="removeStop(stopIndex)"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <template v-if="persistedLines.length">
                  <v-subheader class="px-0">
                    {{ $t("STOCK.TRANSPORT_ORDERS.STOP_ALLOCATIONS") }}
                  </v-subheader>
                  <v-simple-table dense class="mb-4 stop-allocations-table">
                    <thead>
                      <tr>
                        <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                        <th v-for="(stop, stopIndex) in stops" :key="stop._key">
                          {{ stopIndex + 1 }}.
                          {{
                            stop.stop_type === "loading"
                              ? $t("STOCK.TRANSPORT_ORDERS.STOP_TYPE_LOADING")
                              : $t("STOCK.TRANSPORT_ORDERS.STOP_TYPE_UNLOADING")
                          }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="line in persistedLines" :key="`alloc-${line.id}`">
                        <td>{{ lineProductLabel(line) }}</td>
                        <td v-for="(stop, stopIndex) in stops" :key="stop._key + '-' + stopIndex">
                          <StockLineField
                            type="number"
                            :value="stopAllocationQuantity(stop, line)"
                            :disabled="!canEditLines"
                            @input="setStopAllocationQuantity(stop, line, $event)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </template>
                <v-alert v-else type="info" dense text class="mb-4">
                  {{ $t("STOCK.TRANSPORT_ORDERS.STOP_ALLOCATIONS_HINT") }}
                </v-alert>
              <v-row>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="document.goods_description"
                    rows="2"
                    auto-grow
                    :label="$t('STOCK.TRANSPORT_ORDERS.GOODS_DESCRIPTION')"
                    :disabled="headerFieldDisabled('goods_description')"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.approximate_weight_kg"
                    type="number"
                    :label="$t('STOCK.TRANSPORT_ORDERS.APPROX_WEIGHT')"
                    suffix="kg"
                    :disabled="headerFieldDisabled('approximate_weight_kg')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model.number="document.approximate_pallets"
                    type="number"
                    :label="$t('STOCK.TRANSPORT_ORDERS.APPROX_PALLETS')"
                    :disabled="headerFieldDisabled('approximate_pallets')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="document.pallet_type"
                    :items="palletTypeOptions"
                    item-text="label"
                    item-value="value"
                    :label="$t('STOCK.TRANSPORT_ORDERS.PALLET_TYPE')"
                    :disabled="headerFieldDisabled('pallet_type')"
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.truck_plate"
                    :label="$t('STOCK.TRANSPORT_ORDERS.TRUCK_PLATE')"
                    :disabled="headerFieldDisabled('truck_plate')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="document.vehicle_type"
                    :label="$t('STOCK.TRANSPORT_ORDERS.VEHICLE_TYPE')"
                    :disabled="headerFieldDisabled('vehicle_type')"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="document.driver_name"
                    :label="$t('STOCK.TRANSPORT_ORDERS.DRIVER_NAME')"
                    :disabled="headerFieldDisabled('driver_name')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="document.agreed_cost_net"
                    type="number"
                    :label="$t('STOCK.TRANSPORT_ORDERS.AGREED_COST')"
                    :suffix="document.currency_code || ''"
                    :disabled="headerFieldDisabled('agreed_cost_net')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="document.final_cost_net"
                    type="number"
                    :label="$t('STOCK.TRANSPORT_ORDERS.FINAL_COST')"
                    :suffix="document.currency_code || ''"
                    :disabled="headerFieldDisabled('final_cost_net')"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-textarea
                    v-model="document.notes"
                    rows="1"
                    auto-grow
                    :label="$t('GENERAL.LABELS.NOTES')"
                    :disabled="headerFieldDisabled('notes')"
                  ></v-textarea>
                </v-col>
              </v-row>

              <StockTransportModeFields
                v-model="document.mode_data"
                :transport-mode="document.transport_mode"
                :disabled="headerFieldDisabled('mode_data')"
              />

              <v-alert
                v-if="isAmendMode"
                type="info"
                dense
                text
                class="mt-4 mb-2"
              >
                {{ $t("STOCK.TRANSPORT_ORDERS.AMEND_HINT") }}
              </v-alert>

              <v-subheader class="px-0 mt-4 d-flex align-center">
                <span>{{ $t("STOCK.TRANSPORT_ORDERS.LINES") }}</span>
                <v-chip v-if="isAmendMode" x-small class="ml-2" color="primary" outlined>
                  {{ $t("STOCK.TRANSPORT_ORDERS.EXECUTION_PHASE") }}
                </v-chip>
                <v-chip v-else-if="document.status === 'draft'" x-small class="ml-2" outlined>
                  {{ $t("STOCK.TRANSPORT_ORDERS.REQUEST_PHASE") }}
                </v-chip>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="canImportFromVirtualReceipt"
                  small
                  outlined
                  color="primary"
                  :loading="isImportingVirtualReceipt"
                  @click="openImportVirtualReceiptDialog"
                >
                  <v-icon left small>mdi-database-import-outline</v-icon>
                  {{ $t("STOCK.TRANSPORT_ORDERS.IMPORT_FROM_VIRTUAL_RECEIPT") }}
                </v-btn>
              </v-subheader>
              <v-alert v-if="!lines.length" type="info" dense text class="mb-3">
                {{ $t("STOCK.TRANSPORT_ORDERS.NO_LINES") }}
              </v-alert>
              <v-alert
                v-if="lines.length && isOutboundLotSelection && canEditLines && outboundLotSelectionBlocked"
                type="warning"
                dense
                text
                class="mb-3"
              >
                {{ $t("STOCK.TRANSPORT_ORDERS.OUTBOUND_LOT_WAREHOUSE_REQUIRED") }}
              </v-alert>
              <v-simple-table v-if="lines.length" dense class="stock-document-lines mb-4">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                    <th>{{ $t("STOCK.TRANSPORT_ORDERS.PLANNED_QTY") }}</th>
                    <th>{{ $t("STOCK.COMMON.GROSS_WEIGHT_KG") }}</th>
                    <th>{{ $t("STOCK.RECEIPTS.LOT_NUMBER") }}</th>
                    <th>{{ $t("STOCK.INVENTORY.PRODUCTION_DATE") }}</th>
                    <th>{{ $t("STOCK.RECEIPTS.EXPIRY") }}</th>
                    <th>{{ $t("STOCK.TRANSPORT_ORDERS.PALLET_COUNT") }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(line, index) in lines"
                    :key="`line-${index}-${line.stock_product_id}`"
                    :class="{
                      'stock-document-lines__row--lot-error':
                        isOutboundLotSelection &&
                        lineExceedsLotAvailable(line, lotQuantityFieldKey),
                    }"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ lineProductLabel(line) }}</td>
                    <td class="stock-document-lines__col-qty">
                      <StockLineField
                        v-model.number="line.planned_quantity"
                        type="number"
                        :hint="
                          isOutboundLotSelection && line.stock_lot_id
                            ? lineLotQuantityHint(line, lotQuantityFieldKey, lineUnitCode(line))
                            : lineUnitCode(line)
                        "
                        :error="
                          isOutboundLotSelection &&
                          lineExceedsLotAvailable(line, lotQuantityFieldKey)
                        "
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                        @input="recalculateGrossWeightFromLot(line)"
                      />
                    </td>
                    <td class="stock-document-lines__col-qty">
                      <StockLineField
                        v-model.number="line.gross_weight_kg"
                        type="number"
                        hint="kg"
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                      />
                    </td>
                    <td class="stock-document-lines__col-lot">
                      <template v-if="isOutboundLotSelection">
                        <v-autocomplete
                          v-if="canEditLines && lotOptionsForLine(line).length"
                          v-model="line.stock_lot_id"
                          :items="lotOptionsForLine(line)"
                          item-text="label"
                          item-value="id"
                          dense
                          hide-details
                          solo
                          flat
                          clearable
                          class="stock-line-field__input"
                          :error="lineExceedsLotAvailable(line, lotQuantityFieldKey)"
                          :placeholder="$t('STOCK.SALES.LOT_PLACEHOLDER')"
                          @change="onLotSelected(line, $event)"
                        ></v-autocomplete>
                        <span v-else>{{ lineLotLabel(line) }}</span>
                      </template>
                      <StockLineField
                        v-else
                        v-model="line.lot_number"
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                      />
                    </td>
                    <td class="stock-document-lines__col-date">
                      <span v-if="isOutboundLotSelection">{{ formatDisplayDate(line.production_date) }}</span>
                      <StockLineField
                        v-else
                        v-model="line.production_date"
                        type="date"
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                      />
                    </td>
                    <td class="stock-document-lines__col-date">
                      <span v-if="isOutboundLotSelection">{{ formatDisplayDate(line.expiry_date) }}</span>
                      <StockLineField
                        v-else
                        v-model="line.expiry_date"
                        type="date"
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                      />
                    </td>
                    <td class="stock-document-lines__col-qty">
                      <StockLineField
                        v-model.number="line.pallet_count"
                        type="number"
                        :disabled="!canEditLines"
                        @focus="focusSelectAllInput"
                      />
                    </td>
                    <td class="stock-document-lines__actions text-nowrap">
                      <v-btn
                        v-if="canEditLines && line.stock_product_id"
                        icon
                        small
                        :title="$t('STOCK.TRANSPORT_ORDERS.DUPLICATE_PRODUCT_LINE')"
                        @click="duplicateProductLine(index)"
                      >
                        <v-icon small>mdi-plus</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canEditLines"
                        icon
                        small
                        @click="removeLine(index)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <StockProductLineFinder
                v-if="canEditLines"
                :disabled="!canEditLines"
                @add="addProductFromFinder"
              />

              <v-subheader v-if="statusTimeline.length" class="px-0 mt-4">
                {{ $t("STOCK.PURCHASE_ORDERS.STATUS_TIMELINE") }}
              </v-subheader>
              <v-timeline v-if="statusTimeline.length" dense class="to-status-timeline">
                <v-timeline-item
                  v-for="entry in statusTimeline"
                  :key="entry.id"
                  small
                  :color="timelineEntryColor(entry)"
                >
                  <div class="subtitle-2">
                    {{ timelineEntryLabel(entry) }}
                  </div>
                  <div class="caption grey--text">
                    {{ formatTimelineDate(entry.changed_at) }}
                    <span v-if="entry.changed_by_user && entry.changed_by_user.name">
                      · {{ entry.changed_by_user.name }}
                    </span>
                  </div>
                  <div v-if="entry.notes" class="caption">{{ entry.notes }}</div>
                </v-timeline-item>
              </v-timeline>

              <StockDocumentChainLinks
                v-if="!isNew"
                :show-purchase-orders="document.direction === 'inbound'"
                :show-sales-orders="document.direction === 'outbound'"
                show-stock-actions
                :purchase-orders="linkedPurchaseOrders"
                :sales-orders="linkedSalesOrders"
                :stock-actions="linkedStockActions"
                :stock-actions-title="linkedStockActionsTitle"
                :stock-actions-empty-message="linkedStockActionsEmptyMessage"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="!isNew" value="shipments" eager>
          <v-card flat>
            <v-card-text>
              <StockShipmentsPanel
                :transport-order-id="transportOrderId"
                :shipments="shipments"
                :can-create="canCreateShipment"
                :can-edit="canEditShipment"
                :supports-cmr="supportsCmr"
                @updated="shipments = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>

        <v-tab-item v-if="!isNew" value="files">
          <v-card flat>
            <v-card-text>
              <StockDocumentFilesPanel
                document-kind="transport_order"
                :document-id="transportOrderId"
                :files="documentFiles"
                :related-files="relatedDocumentFiles"
                :can-regenerate="canRegenerateDocument"
                @files-updated="documentFiles = $event"
              />
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <v-dialog v-model="showReceiveToWarehouseDialog" max-width="520">
      <v-card>
        <v-card-title>{{ $t("STOCK.TRANSPORT_ORDERS.RECEIVE_TO_WAREHOUSE") }}</v-card-title>
        <v-card-text>
          <p v-if="convertibleTransitReceipt" class="mb-4">
            {{ $t("STOCK.TRANSPORT_ORDERS.RECEIVE_TO_WAREHOUSE_HINT", {
              code: convertibleTransitReceipt.code,
            }) }}
          </p>
          <v-autocomplete
            v-model="receivePhysicalWarehouseId"
            :items="receivePhysicalWarehouseOptions"
            item-text="label"
            item-value="id"
            :label="$t('STOCK.RECEIPTS.PHYSICAL_DESTINATION_WAREHOUSE')"
            dense
            outlined
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showReceiveToWarehouseDialog = false">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="success"
            :disabled="!receivePhysicalWarehouseId"
            :loading="isProcessing"
            @click="receiveToWarehouse"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.RECEIVE_TO_WAREHOUSE") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showImportVirtualReceiptDialog" max-width="640">
      <v-card>
        <v-card-title>{{ $t("STOCK.TRANSPORT_ORDERS.IMPORT_VIRTUAL_RECEIPT_TITLE") }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ $t("STOCK.TRANSPORT_ORDERS.IMPORT_FROM_VIRTUAL_RECEIPT_HINT") }}</p>
          <v-radio-group v-model="selectedImportReceiptId" hide-details>
            <v-radio
              v-for="receipt in importableVirtualReceipts"
              :key="receipt.id"
              :value="receipt.id"
            >
              <template #label>
                <div>
                  <span class="font-weight-medium">{{ receipt.code }}</span>
                  <span class="grey--text text--darken-1">
                    · {{ formatDisplayDate(receipt.action_date) }}
                    <template v-if="receipt.destination_warehouse">
                      · {{ receipt.destination_warehouse.name }}
                    </template>
                    ·
                    {{
                      $t("STOCK.TRANSPORT_ORDERS.VIRTUAL_RECEIPT_LOT_COUNT", {
                        count: (receipt.items || []).length,
                      })
                    }}
                  </span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showImportVirtualReceiptDialog = false">
            {{ $t("GENERAL.LABELS.CANCEL") }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!selectedImportReceiptId"
            :loading="isImportingVirtualReceipt"
            @click="importVirtualReceipt"
          >
            {{ $t("STOCK.TRANSPORT_ORDERS.IMPORT_FROM_VIRTUAL_RECEIPT") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockDocumentFilesPanel from "@/views/partials/stock/StockDocumentFilesPanel.vue";
import StockTransportModeFields from "@/views/partials/stock/StockTransportModeFields.vue";
import StockDealReference from "@/views/partials/stock/StockDealReference.vue";
import StockDocumentChainLinks from "@/views/partials/stock/StockDocumentChainLinks.vue";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockShipmentsPanel from "@/views/partials/stock/shipments/StockShipmentsPanel.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentOptionsMixin from "@/views/partials/stock/stockDocumentOptions.mixin";
import stockDocumentContextMixin from "@/views/partials/stock/stockDocumentContext.mixin";
import stockLotQuantityValidationMixin from "@/views/partials/stock/stockLotQuantityValidation.mixin";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import {
  GET_STOCK_TRANSPORT_ORDER,
  GET_STOCK_TRANSPORT_DIRECTIONS,
  GET_STOCK_TRANSPORT_MODES,
  CREATE_STOCK_TRANSPORT_ORDER,
  UPDATE_STOCK_TRANSPORT_ORDER,
  CANCEL_STOCK_TRANSPORT_ORDER,
  DISCARD_STOCK_TRANSPORT_ORDER,
  SEND_STOCK_TRANSPORT_ORDER,
  CONFIRM_STOCK_TRANSPORT_ORDER,
  MARK_STOCK_TRANSPORT_IN_TRANSIT,
  MARK_STOCK_TRANSPORT_DELIVERED,
  GET_STOCK_TRANSPORT_ORDER_DRAFT_FROM_PO,
  GET_STOCK_TRANSPORT_ORDER_DRAFT_FROM_SO,
  GET_STOCK_TRANSPORT_ORDER_SUGGEST_LOTS,
  GET_STOCK_TRANSPORT_ORDER_IMPORTABLE_VIRTUAL_RECEIPTS,
  IMPORT_VIRTUAL_RECEIPT_INTO_TRANSPORT_ORDER,
} from "@/store/stock/stockTransportOrders.module";
import {
  GET_STOCK_DEAL_OPTIONS,
  CONVERT_STOCK_RECEIPT_TO_PHYSICAL,
} from "@/store/stock/stockReceipts.module";
import { GET_STOCK_WAREHOUSE_OPTIONS } from "@/store/stock/stockWarehouses.module";
import { SHOW_SNACK } from "@/store/snack.module";

const emptyLine = () => ({
  stock_product_id: null,
  planned_quantity: 1,
  net_weight_kg: null,
  gross_weight_kg: null,
  gross_to_net_ratio: null,
  gross_weight_source: null,
  stock_lot_id: null,
  lot_number: "",
  production_date: null,
  expiry_date: null,
  pallet_count: null,
  unit_id: null,
  unit_code: null,
  notes: null,
});


export default {
  name: "StockViewTransportOrder",
  components: {
    StockProductLineFinder,
    StockLineField,
    StockDocumentFilesPanel,
    StockTransportModeFields,
    StockDealReference,
    StockDocumentChainLinks,
    StockDocumentPageHeading,
    StockShipmentsPanel,
    DatePicker,
  },
  mixins: [
    stockDocumentLinesMixin,
    stockDocumentOptionsMixin,
    stockDocumentContextMixin,
    stockLotQuantityValidationMixin,
  ],
  data() {
    return {
      lotQuantityFieldKey: "planned_quantity",
      tab: "details",
      isProcessing: false,
      document: this.emptyDocument(),
      lines: [],
      stops: [],
      advancedStopsMode: false,
      stopKeyCounter: 0,
      stopWarehouseOptions: [],
      refs: [],
      linkedStockActions: [],
      linkedPurchaseOrders: [],
      linkedSalesOrders: [],
      shipments: [],
      organizations: [],
      companyOptions: [],
      dealOptions: [],
      companySearch: null,
      validationErrors: {},
      documentFiles: [],
      lookupDirections: [],
      lookupModes: [],
      lotSuggestions: [],
      showReceiveToWarehouseDialog: false,
      receivePhysicalWarehouseId: null,
      receivePhysicalWarehouseOptions: [],
      showImportVirtualReceiptDialog: false,
      importableVirtualReceipts: [],
      selectedImportReceiptId: null,
      isImportingVirtualReceipt: false,
    };
  },
  computed: {
    isOutboundLotSelection() {
      return this.document.direction === "outbound";
    },
    loadingStops() {
      return this.stops.filter((stop) => stop.stop_type === "loading");
    },
    unloadingStops() {
      return this.stops.filter((stop) => stop.stop_type === "unloading");
    },
    isMultiStop() {
      return (
        this.advancedStopsMode ||
        this.loadingStops.length > 1 ||
        this.unloadingStops.length > 1
      );
    },
    persistedLines() {
      return this.lines.filter((line) => line.id && line.stock_product_id);
    },
    stopAddressSourceOptions() {
      return [
        { value: "warehouse", text: this.$t("STOCK.TRANSPORT_ORDERS.STOP_SOURCE_WAREHOUSE") },
        { value: "company", text: this.$t("STOCK.TRANSPORT_ORDERS.STOP_SOURCE_COMPANY") },
        { value: "free_text", text: this.$t("STOCK.TRANSPORT_ORDERS.STOP_SOURCE_FREE_TEXT") },
      ];
    },
    stopWarnings() {
      return Array.isArray(this.document.stop_warnings) ? this.document.stop_warnings : [];
    },
    isDirectionLocked() {
      if (this.document.direction_locked) {
        return true;
      }
      return (this.refs || []).some((ref) =>
        ["stock_purchase_order", "stock_sales_order"].includes(ref.referencable_type)
      );
    },
    outboundSourceWarehouseId() {
      const ref = (this.refs || []).find(
        (row) => row.referencable_type === "stock_sales_order"
      );
      const warehouseId = ref?.referencable?.source_warehouse_id;
      return warehouseId ? Number(warehouseId) : null;
    },
    outboundLotSelectionBlocked() {
      return this.isOutboundLotSelection && !this.outboundSourceWarehouseId;
    },
    isNew() {
      return !this.$route.params.id;
    },
    isAmendMode() {
      return ["sent", "agreed", "in_transit"].includes(this.document.status);
    },
    canEditDocument() {
      if (this.isNew) {
        return this.hasPermissions("stock-transport-orders-create");
      }
      if (!this.hasPermissions("stock-transport-orders-edit")) {
        return false;
      }
      return this.document.status === "draft" || this.isAmendMode;
    },
    canEditLines() {
      return this.canEditDocument;
    },
    canSend() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-transport-orders-edit")
      );
    },
    canConfirm() {
      return (
        !this.isNew &&
        this.document.status === "sent" &&
        this.hasPermissions("stock-transport-orders-edit")
      );
    },
    canMarkInTransit() {
      return (
        !this.isNew &&
        this.document.status === "agreed" &&
        this.hasPermissions("stock-transport-orders-edit")
      );
    },
    canMarkDelivered() {
      return (
        !this.isNew &&
        this.document.status === "in_transit" &&
        this.hasPermissions("stock-transport-orders-edit")
      );
    },
    canCreateReceipt() {
      return (
        !this.isNew &&
        this.document.direction === "inbound" &&
        ["agreed", "in_transit"].includes(this.document.status) &&
        this.hasPermissions("stock-receipts-create")
      );
    },
    canCreateDelivery() {
      return (
        !this.isNew &&
        this.document.direction === "outbound" &&
        ["agreed", "in_transit"].includes(this.document.status) &&
        this.hasPermissions("stock-sales-create")
      );
    },
    hasTransitVirtualReceipt() {
      return (this.linkedStockActions || []).some((action) => {
        if (action.kind !== "receipt") {
          return false;
        }
        if (["cancelled", "reversed"].includes(action.status)) {
          return false;
        }
        return action.destination_warehouse?.virtual_subtype === "transit";
      });
    },
    canBookToTransit() {
      return (
        !this.isNew &&
        !this.isMultiStop &&
        this.document.direction === "inbound" &&
        this.document.status === "in_transit" &&
        !this.hasTransitVirtualReceipt &&
        this.hasPermissions("stock-receipts-create")
      );
    },
    convertibleTransitReceipt() {
      return (this.linkedStockActions || []).find(
        (action) => action.can_convert_to_physical
      );
    },
    canReceiveToWarehouse() {
      return (
        !!this.convertibleTransitReceipt &&
        this.hasPermissions("stock-corrections-create")
      );
    },
    hasSalesOrderRef() {
      return (this.refs || []).some(
        (ref) => ref.referencable_type === "stock_sales_order"
      );
    },
    canImportFromVirtualReceipt() {
      return (
        !this.isNew &&
        this.document.direction === "outbound" &&
        this.canEditLines &&
        this.hasSalesOrderRef &&
        this.hasPermissions("stock-transport-orders-edit")
      );
    },
    linkedStockActionsTitle() {
      if (this.document.direction === "inbound") {
        return this.$t("STOCK.TRANSPORT_ORDERS.RECEIPTS_FROM_TRANSPORT");
      }
      if (this.document.direction === "outbound") {
        return this.$t("STOCK.TRANSPORT_ORDERS.DELIVERIES_FROM_TRANSPORT");
      }
      return this.$t("STOCK.TRANSPORT_ORDERS.LINKED_FROM_TRANSPORT");
    },
    linkedStockActionsEmptyMessage() {
      if (this.document.direction === "inbound") {
        return this.$t("STOCK.COMMON.NO_RELATED_RECEIPTS");
      }
      if (this.document.direction === "outbound") {
        return this.$t("STOCK.COMMON.NO_RELATED_DELIVERIES");
      }
      return this.$t("STOCK.TRANSPORT_ORDERS.NO_REFERENCES");
    },
    canDiscard() {
      return (
        !this.isNew &&
        this.document.status === "draft" &&
        this.hasPermissions("stock-transport-orders-cancel")
      );
    },
    canCancel() {
      return (
        !this.isNew &&
        !["draft", "cancelled", "delivered"].includes(this.document.status) &&
        this.hasPermissions("stock-transport-orders-cancel")
      );
    },
    canRegenerateDocument() {
      return ["sent", "agreed", "in_transit", "delivered"].includes(this.document.status);
    },
    relatedDocumentFiles() {
      const files = [];
      (this.shipments || []).forEach((shipment) => {
        const relatedLabel = this.shipmentRelatedLabel(shipment);
        const cmr = shipment.cmr_document || {};
        const cmrFile = cmr.generated_file || cmr.draft_pdf_file;
        if (cmrFile && cmrFile.id) {
          files.push({
            ...cmrFile,
            shipment_id: shipment.id,
            related_kind: "cmr",
            related_label: relatedLabel,
          });
        }
        const tradeFile = shipment.trade_document && shipment.trade_document.generated_file;
        if (tradeFile && tradeFile.id) {
          files.push({
            ...tradeFile,
            shipment_id: shipment.id,
            related_kind: "trade",
            related_label: relatedLabel,
          });
        }
      });
      return files;
    },
    supportsCmr() {
      if (this.document.supports_cmr != null) {
        return !!this.document.supports_cmr;
      }
      const mode = this.lookupModes.find(
        (item) => item.slug === this.document.transport_mode
      );
      return mode ? !!mode.supports_cmr : false;
    },
    canCreateShipment() {
      return !this.isNew && this.hasPermissions("stock-cmr-create");
    },
    canEditShipment() {
      return (
        !this.isNew &&
        (this.hasPermissions("stock-cmr-edit") || this.hasPermissions("stock-cmr-create"))
      );
    },
    statusColor() {
      const map = {
        draft: "grey",
        sent: "grey",
        agreed: "primary",
        in_transit: "orange",
        delivered: "success",
        cancelled: "error",
      };
      return map[this.document.status] || "grey";
    },
    statusLabel() {
      const key = `STOCK.STATUSES.TO.${(this.document.status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : this.document.status;
    },
    transportOrderId() {
      const id = this.$route.params.id;
      return id ? Number(id) : null;
    },
    directionOptions() {
      const items = this.lookupDirections.length
        ? this.lookupDirections
        : ["inbound", "outbound", "direct"].map((slug) => ({ slug }));

      return items.map((item) => ({
        value: item.slug,
        label: this.$te(`STOCK.TRANSPORT_ORDERS.DIRECTIONS.${item.slug.toUpperCase()}`)
          ? this.$t(`STOCK.TRANSPORT_ORDERS.DIRECTIONS.${item.slug.toUpperCase()}`)
          : item.name || item.slug,
      }));
    },
    transportModeOptions() {
      const items = this.lookupModes.length
        ? this.lookupModes
        : [
            "road",
            "container_road",
            "container_sea",
            "air_cargo",
            "rail",
            "multimodal",
            "other",
          ].map((slug) => ({ slug }));

      return items.map((item) => ({
        value: item.slug,
        label: this.$te(`STOCK.TRANSPORT_ORDERS.MODES.${item.slug.toUpperCase()}`)
          ? this.$t(`STOCK.TRANSPORT_ORDERS.MODES.${item.slug.toUpperCase()}`)
          : item.name || item.slug,
      }));
    },
    statusTimeline() {
      const timeline = this.document.status_timeline;
      if (!Array.isArray(timeline)) {
        return [];
      }
      return [...timeline].sort(
        (a, b) => new Date(b.changed_at) - new Date(a.changed_at)
      );
    },
    palletTypeOptions() {
      return ["euro", "industrial", "other"].map((value) => ({
        value,
        label: this.$t(`STOCK.TRANSPORT_ORDERS.PALLET_TYPES.${value.toUpperCase()}`),
      }));
    },
  },
  watch: {
    "document.organization_id"() {
      this.runStockHeaderReloadOnOrganizationChange();
      this.loadStopWarehouseOptions();
    },
  },
  created() {
    this.debouncedLoadCompanies = debounce(function () {
      this.loadCompanies();
    }, 300);
    if (this.isNew) {
      this.ensureDefaultStops();
    }
  },
  mounted() {
    this.bootstrap();
  },
  beforeDestroy() {
    if (this.debouncedLoadCompanies && this.debouncedLoadCompanies.cancel) {
      this.debouncedLoadCompanies.cancel();
    }
  },
  methods: {
    shipmentRelatedLabel(shipment) {
      const no = shipment && shipment.shipment_no;
      const label = shipment && shipment.label ? ` — ${shipment.label}` : "";
      return this.$t("STOCK.SHIPMENTS.TITLE_ITEM", { no }) + label;
    },
    emptyDocument() {
      return {
        organization_id: null,
        carrier_company_id: null,
        deal_id: null,
        direction: "inbound",
        direction_locked: false,
        transport_mode: "road",
        status: "draft",
        code: null,
        loading_address: null,
        unloading_address: null,
        goods_description: null,
        approximate_weight_kg: null,
        approximate_pallets: null,
        pallet_type: null,
        scheduled_loading_at: null,
        eta_at: null,
        agreed_cost_net: null,
        final_cost_net: null,
        currency_code: "EUR",
        truck_plate: null,
        vehicle_type: null,
        driver_name: null,
        mode_data: {},
        notes: null,
      };
    },
    normalizeModeData(value) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return value;
      }
      return {};
    },
    async bootstrap() {
      this.isProcessing = true;
      try {
        await Promise.all([
          this.loadOrganizations(),
          this.loadCompanies(),
          this.loadDeals(),
          this.loadLookupOptions(),
        ]);

        if (!this.isNew) {
          const data = await this.$store.dispatch(
            GET_STOCK_TRANSPORT_ORDER,
            this.$route.params.id
          );
          await this.applyDocument(data);
        } else {
          this.ensureDefaultStops();
          await this.withSuppressedStockHeaderReload(async () => {
            if (this.hasStockDraftSourceQuery()) {
              await this.loadDraftFromQuery();
            } else {
              this.applyStockContextDefaultsOnNewDocument();
            }
          });
        }
        await this.loadStopWarehouseOptions();
        if (this.isNew) {
          this.ensureDefaultStops();
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async applyDocument(data) {
      await this.withSuppressedStockHeaderReload(async () => {
        this.document = {
          ...this.emptyDocument(),
          ...data,
          mode_data: this.normalizeModeData(data.mode_data),
        };
        await this.reloadTransportOrderHeaderOptions();
        if (this.isNew) {
          this.setHeaderOptionsSeed(data, "syncTransportOrderHeaderOptions");
        } else {
          this.clearHeaderOptionsSeed();
        }
        this.syncTransportOrderHeaderOptions(data);
      });
      this.lines = (data.items || []).map((item) => {
        const line = {
          id: item.id,
          stock_product_id: item.stock_product_id,
          planned_quantity: item.planned_quantity,
          net_weight_kg: item.net_weight_kg,
          gross_weight_kg: item.gross_weight_kg,
          gross_to_net_ratio: item.gross_to_net_ratio,
          gross_weight_source: item.gross_weight_source,
          stock_lot_id: null,
          lot_number: item.lot_number || "",
          production_date: item.production_date || null,
          expiry_date: item.expiry_date || null,
          pallet_count: item.pallet_count,
          notes: item.notes,
          stock_product: item.stock_product || null,
          purchase_order_item_id: item.purchase_order_item_id,
          sales_order_item_id: item.sales_order_item_id,
          ...this.mapLineUnitFields(item),
        };
        return line;
      });
      this.refs = Array.isArray(data.refs) ? data.refs : [];
      if (this.isOutboundLotSelection) {
        await this.loadLotSuggestions();
        this.lines = this.lines.map((line) => ({
          ...line,
          stock_lot_id: this.resolveLineStockLotId(line),
        }));
      }
      this.linkedStockActions = Array.isArray(data.linked_stock_actions)
        ? data.linked_stock_actions
        : [];
      this.linkedPurchaseOrders = Array.isArray(data.linked_purchase_orders)
        ? data.linked_purchase_orders
        : [];
      this.linkedSalesOrders = Array.isArray(data.linked_sales_orders)
        ? data.linked_sales_orders
        : [];
      this.shipments = Array.isArray(data.shipments) ? data.shipments : [];
      this.documentFiles = Array.isArray(data.files) ? data.files : [];
      this.advancedStopsMode = data.is_simple_stop_layout === false;
      this.stops = (Array.isArray(data.stops) ? data.stops : []).map((stop) =>
        this.mapStopFromResponse(stop)
      );
      if (!data.id) {
        this.ensureDefaultStops();
      }
    },
    ensureDefaultStops() {
      if (this.stops.length) {
        return;
      }
      this.stops = [
        this.buildLocalStop(
          "loading",
          this.document.loading_address,
          this.document.scheduled_loading_at
        ),
        this.buildLocalStop(
          "unloading",
          this.document.unloading_address,
          this.document.eta_at
        ),
      ];
    },
    mapStopFromResponse(stop) {
      this.stopKeyCounter += 1;
      const allocations = {};
      (stop.stop_items || []).forEach((stopItem) => {
        allocations[stopItem.stock_transport_order_item_id] = stopItem.quantity;
      });
      this.seedStopCompanyOption(stop);
      return {
        id: stop.id,
        _key: `stop-${stop.id}-${this.stopKeyCounter}`,
        stop_no: stop.stop_no,
        stop_type: stop.stop_type,
        stock_warehouse_id: stop.stock_warehouse_id,
        company_id: stop.company_id,
        address_override: stop.address_override,
        goods_description: stop.goods_description || null,
        reference_number: stop.reference_number || null,
        scheduled_at: stop.scheduled_at,
        notes: stop.notes,
        is_fulfilled: stop.is_fulfilled,
        fulfillment_tracked: stop.fulfillment_tracked,
        address_source: stop.stock_warehouse_id
          ? "warehouse"
          : stop.company_id
          ? "company"
          : "free_text",
        allocations,
      };
    },
    seedStopCompanyOption(stop) {
      if (!stop || !stop.company) {
        return;
      }
      this.companyOptions = this.mergeOptionIntoList(this.companyOptions, {
        id: stop.company.id,
        label: stop.company.label || stop.company.name,
        ...stop.company,
      });
    },
    buildLocalStop(stopType, addressOverride = null, scheduledAt = null) {
      this.stopKeyCounter += 1;
      return {
        id: null,
        _key: `stop-new-${this.stopKeyCounter}`,
        stop_no: null,
        stop_type: stopType,
        stock_warehouse_id: null,
        company_id: null,
        address_override: addressOverride || null,
        goods_description: null,
        reference_number: null,
        scheduled_at: scheduledAt || null,
        notes: null,
        is_fulfilled: null,
        fulfillment_tracked: null,
        address_source: "free_text",
        allocations: {},
      };
    },
    addStop(stopType) {
      this.stops.push(this.buildLocalStop(stopType));
      this.advancedStopsMode = true;
    },
    removeStop(index) {
      this.stops.splice(index, 1);
    },
    stopPosition(stop) {
      return this.stops.indexOf(stop) + 1;
    },
    canCreateReceiptForStop(stop) {
      return (
        this.isMultiStop &&
        this.canCreateReceipt &&
        stop.stop_type === "unloading"
      );
    },
    canCreateDeliveryForStop(stop) {
      return (
        this.isMultiStop &&
        this.canCreateDelivery &&
        stop.stop_type === "loading"
      );
    },
    isStopFulfillmentTracked(stop) {
      if (typeof stop.fulfillment_tracked === "boolean") {
        return stop.fulfillment_tracked;
      }
      const direction = this.document.direction;
      if (direction === "inbound") {
        return stop.stop_type === "unloading";
      }
      if (direction === "outbound") {
        return stop.stop_type === "loading";
      }
      return true;
    },
    stopAllocationQuantity(stop, line) {
      const value = stop.allocations ? stop.allocations[line.id] : null;
      return value != null ? value : "";
    },
    setStopAllocationQuantity(stop, line, value) {
      if (!stop.allocations) {
        this.$set(stop, "allocations", {});
      }
      this.$set(stop.allocations, line.id, value === "" || value == null ? null : Number(value));
    },
    async loadStopWarehouseOptions() {
      try {
        const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, {
          organization_ids: this.organizationIdsForWarehouseQuery(),
        });
        const warehouses = data.data || data;
        this.stopWarehouseOptions = this.mapWarehouseOptions(warehouses, ["physical", "virtual"]);
      } catch (errors) {
        // Non-blocking: address selection can still fall back to free text.
      }
    },
    onStopAddressSourceChange(stop) {
      this.applyStopSourceAddress(stop);
    },
    onStopWarehouseChange(stop) {
      this.applyStopSourceAddress(stop);
    },
    onStopCompanyChange(stop) {
      this.applyStopSourceAddress(stop);
    },
    applyStopSourceAddress(stop) {
      if (!stop || stop.address_source === "free_text") {
        return;
      }
      this.$set(stop, "address_override", this.stopSourceAddress(stop));
    },
    stopSourceAddress(stop) {
      if (!stop) {
        return null;
      }
      if (stop.address_source === "warehouse") {
        return this.optionAddress(
          (this.stopWarehouseOptions || []).find(
            (item) => Number(item.id) === Number(stop.stock_warehouse_id)
          )
        );
      }
      if (stop.address_source === "company") {
        return this.optionAddress(this.findCompanyOption(stop.company_id));
      }
      return null;
    },
    optionAddress(option) {
      if (!option || option.address == null) {
        return null;
      }
      const address = String(option.address).trim();
      return address || null;
    },
    payload() {
      const {
        organization_id,
        carrier_company_id,
        deal_id,
        direction,
        transport_mode,
        loading_address,
        unloading_address,
        goods_description,
        approximate_weight_kg,
        approximate_pallets,
        pallet_type,
        scheduled_loading_at,
        eta_at,
        agreed_cost_net,
        final_cost_net,
        currency_code,
        truck_plate,
        vehicle_type,
        driver_name,
        mode_data,
        notes,
      } = this.document;

      const result = {
        organization_id,
        carrier_company_id,
        deal_id,
        direction,
        transport_mode,
        loading_address,
        unloading_address,
        goods_description,
        approximate_weight_kg,
        approximate_pallets,
        pallet_type,
        scheduled_loading_at,
        eta_at,
        agreed_cost_net,
        final_cost_net,
        currency_code,
        truck_plate,
        vehicle_type,
        driver_name,
        mode_data: this.normalizeModeData(mode_data),
        notes,
        refs: (this.refs || []).map((ref) => ({
          referencable_type: ref.referencable_type,
          referencable_id: ref.referencable_id,
        })),
        items: this.lines
          .filter((line) => line.stock_product_id)
          .map((line, index) => ({
            id: line.id || undefined,
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            planned_quantity: line.planned_quantity,
            net_weight_kg: line.planned_quantity,
            gross_weight_kg: line.gross_weight_kg,
            lot_number: line.lot_number || undefined,
            production_date: line.production_date || undefined,
            expiry_date: line.expiry_date || undefined,
            pallet_count: line.pallet_count != null ? line.pallet_count : undefined,
            unit_id: line.unit_id,
            purchase_order_item_id: line.purchase_order_item_id || undefined,
            sales_order_item_id: line.sales_order_item_id || undefined,
            notes: line.notes || undefined,
          })),
      };

      result.stops = this.stops.map((stop, index) => ({
        id: stop.id || undefined,
        stop_no: index + 1,
        stop_type: stop.stop_type,
        stock_warehouse_id: stop.address_source === "warehouse" ? stop.stock_warehouse_id : null,
        company_id: stop.address_source === "company" ? stop.company_id : null,
        address_override: stop.address_override || null,
        goods_description: stop.goods_description || null,
        reference_number: stop.reference_number || null,
        scheduled_at: stop.scheduled_at || null,
        notes: stop.notes || null,
        allocations: Object.entries(stop.allocations || {})
          .filter(([, quantity]) => quantity != null && Number(quantity) > 0)
          .map(([lineId, quantity]) => ({
            stock_transport_order_item_id: Number(lineId),
            quantity: Number(quantity),
          })),
      }));

      if (this.loadingStops.length === 1) {
        result.loading_address = this.loadingStops[0].address_override || null;
      }
      if (this.unloadingStops.length === 1) {
        result.unloading_address = this.unloadingStops[0].address_override || null;
      }

      return result;
    },
    async save() {
      if (!this.document.organization_id) {
        this.validationErrors = {
          organization_id: [this.$t("GENERAL.VALIDATION.REQUIRED_FIELD")],
        };
        return;
      }
      if (
        this.isOutboundLotSelection &&
        !this.validateLotQuantitiesOrNotify(this.lotQuantityFieldKey)
      ) {
        return;
      }
      this.isProcessing = true;
      try {
        this.validationErrors = {};
        const params = this.payload();
        let data;
        if (this.isNew) {
          data = await this.$store.dispatch(CREATE_STOCK_TRANSPORT_ORDER, params);
          this.$router.replace({
            name: "stock-transport-orders-view",
            params: { id: data.id },
          });
        } else {
          data = await this.$store.dispatch(UPDATE_STOCK_TRANSPORT_ORDER, {
            id: this.$route.params.id,
            params,
          });
        }
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
      } catch (errors) {
        const errorData = errors?.response?.data || errors || {};
        this.validationErrors = errorData.errors || {};
        const message =
          errorData.message ||
          (errorData.errors && Object.values(errorData.errors).flat()[0]) ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
        this.$store.dispatch(SHOW_SNACK, {
          message,
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async runStatusAction(action, confirmKey, followUpMessageKey) {
      if (confirmKey && !confirm(this.$t(confirmKey))) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(action, this.$route.params.id);
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("GENERAL.SUCCESS_MESSAGE"),
          color: "success",
        });
        if (
          followUpMessageKey &&
          this.document.direction === "inbound" &&
          this.document.status === "in_transit"
        ) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t(followUpMessageKey),
            color: "info",
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    sendOrder() {
      return this.runStatusAction(SEND_STOCK_TRANSPORT_ORDER);
    },
    confirmOrder() {
      return this.runStatusAction(CONFIRM_STOCK_TRANSPORT_ORDER);
    },
    markInTransit() {
      return this.runStatusAction(
        MARK_STOCK_TRANSPORT_IN_TRANSIT,
        null,
        "STOCK.TRANSPORT_ORDERS.BOOK_TO_TRANSIT_HINT"
      );
    },
    markDelivered() {
      return this.runStatusAction(MARK_STOCK_TRANSPORT_DELIVERED);
    },
    cancelOrder() {
      return this.runStatusAction(
        CANCEL_STOCK_TRANSPORT_ORDER,
        "STOCK.TRANSPORT_ORDERS.CONFIRM_CANCEL"
      );
    },
    createReceipt(stop = null) {
      this.$router.push({
        name: "stock-receipts-create",
        query: {
          transport_order_id: this.$route.params.id,
          stop_id: stop ? stop.id : undefined,
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    createTransitReceipt() {
      this.$router.push({
        name: "stock-receipts-create",
        query: {
          transport_order_id: this.$route.params.id,
          receipt_flow: "transit",
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    async openReceiveToWarehouseDialog() {
      if (!this.convertibleTransitReceipt) {
        return;
      }

      this.receivePhysicalWarehouseId = null;
      this.receivePhysicalWarehouseOptions = [];
      this.showReceiveToWarehouseDialog = true;

      try {
        const data = await this.$store.dispatch(GET_STOCK_WAREHOUSE_OPTIONS, {
          organization_ids: [this.document.organization_id].filter(Boolean),
        });
        const warehouses = data.data || data;
        this.receivePhysicalWarehouseOptions = this.mapPhysicalWarehouseOptions(warehouses);

        if (this.receivePhysicalWarehouseOptions.length === 1) {
          this.receivePhysicalWarehouseId = this.receivePhysicalWarehouseOptions[0].id;
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    async receiveToWarehouse() {
      const receipt = this.convertibleTransitReceipt;
      if (!receipt || !this.receivePhysicalWarehouseId) {
        return;
      }

      this.isProcessing = true;
      try {
        const result = await this.$store.dispatch(CONVERT_STOCK_RECEIPT_TO_PHYSICAL, {
          id: receipt.id,
          params: {
            destination_warehouse_id: this.receivePhysicalWarehouseId,
          },
        });
        this.showReceiveToWarehouseDialog = false;
        const data = await this.$store.dispatch(
          GET_STOCK_TRANSPORT_ORDER,
          this.$route.params.id
        );
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.CONVERT_TO_PHYSICAL_SUCCESS"),
          color: "success",
        });
        if (result.physical_receipt && result.physical_receipt.id) {
          await this.$router.push({
            name: "stock-receipts-view",
            params: { id: result.physical_receipt.id },
          });
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    createDelivery(stop = null) {
      this.$router.push({
        name: "stock-sales-create",
        query: {
          transport_order_id: this.$route.params.id,
          stop_id: stop ? stop.id : undefined,
          deal_id: this.document.deal_id || undefined,
          organization_id: this.document.organization_id || undefined,
        },
      });
    },
    async openImportVirtualReceiptDialog() {
      this.isImportingVirtualReceipt = true;
      try {
        const data = await this.$store.dispatch(
          GET_STOCK_TRANSPORT_ORDER_IMPORTABLE_VIRTUAL_RECEIPTS,
          this.$route.params.id
        );
        this.importableVirtualReceipts = Array.isArray(data) ? data : [];

        if (!this.importableVirtualReceipts.length) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.TRANSPORT_ORDERS.NO_IMPORTABLE_VIRTUAL_RECEIPTS"),
            color: "info",
          });
          return;
        }

        this.selectedImportReceiptId =
          this.importableVirtualReceipts.length === 1
            ? this.importableVirtualReceipts[0].id
            : null;
        this.showImportVirtualReceiptDialog = true;
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isImportingVirtualReceipt = false;
      }
    },
    async importVirtualReceipt() {
      if (!this.selectedImportReceiptId) {
        return;
      }

      this.isImportingVirtualReceipt = true;
      try {
        const data = await this.$store.dispatch(
          IMPORT_VIRTUAL_RECEIPT_INTO_TRANSPORT_ORDER,
          {
            id: this.$route.params.id,
            receipt_id: this.selectedImportReceiptId,
          }
        );
        this.showImportVirtualReceiptDialog = false;
        await this.applyDocument(data);
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.TRANSPORT_ORDERS.IMPORT_VIRTUAL_RECEIPT_SUCCESS"),
          color: "success",
        });
      } catch (errors) {
        const errorData = errors?.response?.data || errors || {};
        const message =
          errorData.message ||
          (errorData.errors && Object.values(errorData.errors).flat()[0]) ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
        this.$store.dispatch(SHOW_SNACK, {
          message,
          color: "error",
        });
      } finally {
        this.isImportingVirtualReceipt = false;
      }
    },
    async loadDraftFromQuery() {
      const query = this.$route.query || {};
      const purchaseOrderId = query.purchase_order_id;
      const salesOrderId = query.sales_order_id;

      if (!purchaseOrderId && !salesOrderId) {
        return;
      }

      try {
        const data = purchaseOrderId
          ? await this.$store.dispatch(GET_STOCK_TRANSPORT_ORDER_DRAFT_FROM_PO, purchaseOrderId)
          : await this.$store.dispatch(GET_STOCK_TRANSPORT_ORDER_DRAFT_FROM_SO, salesOrderId);

        await this.applyDocument(data);
      } catch (errors) {
        const message =
          (errors?.errors && Object.values(errors.errors).flat()[0]) ||
          errors?.message ||
          this.$t("GENERAL.UNEXPECTED_ERROR");
        this.$store.dispatch(SHOW_SNACK, {
          message,
          color: "error",
        });
      }
    },
    async discardOrder() {
      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }

      this.isProcessing = true;
      try {
        await this.$store.dispatch(DISCARD_STOCK_TRANSPORT_ORDER, this.$route.params.id);
        this.$router.push({ name: "stock-transport-orders" });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    headerFieldDisabled(field) {
      if (!this.canEditDocument) {
        return true;
      }
      if (field === "direction" && this.isDirectionLocked) {
        return true;
      }
      if (!this.isAmendMode) {
        return false;
      }
      return ![
        "deal_id",
        "goods_description",
        "approximate_weight_kg",
        "approximate_pallets",
        "scheduled_loading_at",
        "eta_at",
        "final_cost_net",
        "truck_plate",
        "vehicle_type",
        "driver_name",
        "notes",
      ].includes(field);
    },
    addProductFromFinder(product) {
      const line = emptyLine();
      line.stock_product_id = product.id;
      line.stock_product = product;
      this.resolveLineUnitFromProduct(line, product);
      this.lines.push(line);
    },
    removeLine(index) {
      this.lines.splice(index, 1);
    },
    duplicateProductLine(index) {
      const line = this.lines[index];
      if (!line?.stock_product_id || !line.stock_product) {
        return;
      }
      const newLine = emptyLine();
      newLine.stock_product_id = line.stock_product_id;
      newLine.stock_product = line.stock_product;
      newLine.unit_id = line.unit_id;
      newLine.unit_code = line.unit_code;
      newLine.unit = line.unit;
      if (line.purchase_order_item_id) {
        newLine.purchase_order_item_id = line.purchase_order_item_id;
      }
      if (line.sales_order_item_id) {
        newLine.sales_order_item_id = line.sales_order_item_id;
      }
      this.lines.splice(index + 1, 0, newLine);
    },
    cmrStatusLabel(status) {
      const key = `STOCK.STATUSES.CMR.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleString();
    },
    formatDisplayDate(value) {
      if (!value) {
        return "-";
      }
      return moment(value).format("DD/MM/YYYY");
    },
    async loadLotSuggestions() {
      if (this.isNew || !this.transportOrderId || !this.isOutboundLotSelection) {
        this.lotSuggestions = [];
        return;
      }

      try {
        const data = await this.$store.dispatch(GET_STOCK_TRANSPORT_ORDER_SUGGEST_LOTS, {
          transportOrderId: this.transportOrderId,
        });
        this.lotSuggestions = Array.isArray(data) ? data : data.lots || data.items || [];
      } catch (e) {
        this.lotSuggestions = [];
      }
    },
    lotOptionsForLine(line) {
      if (!line.stock_product_id) {
        return [];
      }
      return (this.lotSuggestions || [])
        .filter((row) => Number(row.stock_product_id) === Number(line.stock_product_id))
        .map((row) => ({
          id: row.stock_lot_id || row.id,
          label: row.label || row.lot_number || String(row.stock_lot_id || row.id),
          lot_number: row.lot_number,
          production_date: row.production_date,
          expiry_date: row.expiry_date,
          free_quantity: row.free_quantity != null ? Number(row.free_quantity) : null,
          default_gross_to_net_ratio:
            row.default_gross_to_net_ratio != null
              ? Number(row.default_gross_to_net_ratio)
              : null,
        }));
    },
    resolveLineStockLotId(line) {
      if (!line.lot_number) {
        return null;
      }
      const options = this.lotOptionsForLine(line);
      const match = options.find((option) => {
        if (option.lot_number !== line.lot_number) {
          return false;
        }
        if (line.expiry_date && option.expiry_date && option.expiry_date !== line.expiry_date) {
          return false;
        }
        return true;
      });
      return match ? match.id : null;
    },
    onLotSelected(line, lotId) {
      if (!lotId) {
        line.lot_number = "";
        line.production_date = null;
        line.expiry_date = null;
        line.gross_weight_kg = null;
        return;
      }
      const option = this.lotOptionsForLine(line).find(
        (item) => Number(item.id) === Number(lotId)
      );
      if (!option) {
        return;
      }
      line.lot_number = option.lot_number || option.label || "";
      line.production_date = option.production_date || null;
      line.expiry_date = option.expiry_date || null;
      this.recalculateGrossWeightFromLot(line);
    },
    recalculateGrossWeightFromLot(line) {
      const option = this.lotOptionsForLine(line).find(
        (item) => Number(item.id) === Number(line.stock_lot_id)
      );
      const ratio = option?.default_gross_to_net_ratio;
      if (!ratio || Number(ratio) <= 0) {
        line.gross_weight_kg = null;
        return;
      }

      line.gross_weight_kg = Math.round(
        Number(line.planned_quantity || 0) * Number(ratio) * 10000
      ) / 10000;
    },
    lineLotLabel(line) {
      if (line.lot_number) {
        return line.lot_number;
      }
      if (line.stock_lot_id) {
        const option = this.lotOptionsForLine(line).find(
          (item) => Number(item.id) === Number(line.stock_lot_id)
        );
        return option?.label || String(line.stock_lot_id);
      }
      return "-";
    },
    onCompanySearch(value) {
      const search = value || null;
      if (this.companySearch === search) {
        return;
      }
      this.companySearch = search;
      this.debouncedLoadCompanies();
    },
    findCompanyOption(id) {
      if (!id) {
        return null;
      }
      return (this.companyOptions || []).find(
        (item) => Number(item.id) === Number(id)
      );
    },
    preserveSelectedCompanyOptions(fetched) {
      let options = Array.isArray(fetched) ? [...fetched] : [];
      const selected = [
        this.document.carrier_company,
        this.findCompanyOption(this.document.carrier_company_id),
        ...(this.stops || []).map(
          (stop) => stop.company || this.findCompanyOption(stop.company_id)
        ),
      ];

      selected.forEach((option) => {
        if (option && option.id) {
          options = this.mergeOptionIntoList(options, option);
        }
      });

      return options;
    },
    async loadCompanies() {
      const data = await this.$store.dispatch(GET_COMPANIES_OPTIONS, {
        search: this.companySearch,
      });
      this.companyOptions = this.preserveSelectedCompanyOptions(data.data || data || []);
    },
    async loadLookupOptions() {
      try {
        const [directions, modes] = await Promise.all([
          this.$store.dispatch(GET_STOCK_TRANSPORT_DIRECTIONS),
          this.$store.dispatch(GET_STOCK_TRANSPORT_MODES),
        ]);
        this.lookupDirections = Array.isArray(directions) ? directions : [];
        this.lookupModes = Array.isArray(modes) ? modes : [];
      } catch (error) {
        this.lookupDirections = [];
        this.lookupModes = [];
      }
    },
    timelineEntryLabel(entry) {
      const slug = entry.status && entry.status.slug ? entry.status.slug : "";
      const key = `STOCK.STATUSES.TO.${slug.toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : (entry.status && entry.status.name) || slug || "-";
    },
    timelineEntryColor(entry) {
      const slug = entry.status && entry.status.slug;
      const map = {
        draft: "grey",
        sent: "blue",
        agreed: "primary",
        in_transit: "orange darken-2",
        delivered: "success",
        cancelled: "error",
      };
      return map[slug] || "grey";
    },
    formatTimelineDate(value) {
      if (!value) {
        return "-";
      }
      return new Date(value).toLocaleString();
    },
    async loadDeals() {
      const responseData = await this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, {
        organization_id: this.document.organization_id,
        limit: 50,
      });
      this.applyDealOptionsResponse(responseData);
    },
  },
};
</script>

<style scoped>
.stock-view-page {
  padding-bottom: 48px;
  margin-bottom: 24px;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: middle !important;
}
.stock-document-lines__field {
  max-width: 130px;
}
.stock-document-lines__row--lot-error {
  background-color: rgba(244, 67, 54, 0.08);
}
.stops-table th,
.stops-table td,
.stop-allocations-table th,
.stop-allocations-table td {
  vertical-align: middle !important;
}
.stop-address-cell {
  min-width: 220px;
}
.stop-row-actions {
  white-space: nowrap;
}
</style>
